import React, { useState } from 'react';
import { API_BASE } from '../apiBase';
import VideoPromptGenerator from './VideoPromptGenerator';

interface ProductImage {
  url: string;
  alt: string;
}

interface Product {
  id: string;
  title: string;
  handle: string;
  description: string;
  images: ProductImage[];
  variants: Array<{
    id: string;
    title: string;
    price: string;
    image?: ProductImage;
  }>;
  tags: string[];
  productType: string;
  vendor: string;
  metafields?: Record<string, any>;
}

interface AdStorylineShot {
  shotTitle: string;
  scenePrompt: string;
  voiceover: string;
  onScreenText: string;
  purpose: string;
}

interface SceneDownload {
  filename: string;
  content: string;
}

interface SceneProductPlacement {
  id: string;
  name: string;
  role: string;
  fitExplanation: string;
  optional: boolean;
  highlight?: string;
  imageUrl?: string;
  link?: string;
  placementLocation?: string;
  impactNote?: string;
}

interface ScenePlan {
  sceneTitle: string;
  imagePrompt: string;
  visualType: string;
  nonProductVisual?: string;
  notes?: string;
  products: SceneProductPlacement[];
  download?: SceneDownload;
}

interface SceneDownloadsInfo {
  bundle?: SceneDownload;
  individual?: SceneDownload[];
}

interface CatalogProduct {
  id: string;
  name: string;
  description?: string;
  link?: string;
  imageUrl?: string;
  vendor?: string;
  price?: string;
  tags?: string[];
}

interface AIContent {
  carouselPrompts: string[];
  adStoryline?: string;
  adStorylineShots?: AdStorylineShot[];
  caption?: string;
  hashtags?: string[];
  scenes?: ScenePlan[];
  downloads?: SceneDownloadsInfo;
  catalog?: CatalogProduct[];
}

const ProductRetrieval: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());
  const [aiContent, setAiContent] = useState<AIContent | null>(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [showPromptPlanner, setShowPromptPlanner] = useState(false);

  const downloadTextFile = (download?: SceneDownload | null) => {
    if (!download || !download.content) {
      alert('Download not available for this scene yet.');
      return;
    }
    const blob = new Blob([download.content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = download.filename || `scene-brief-${Date.now()}.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const downloadAllBriefs = () => {
    if (!aiContent?.downloads?.bundle) {
      alert('Generate content first to download scene briefs.');
      return;
    }
    downloadTextFile(aiContent.downloads.bundle);
  };

  const searchProducts = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/search-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery, rawInput: searchQuery }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to search products');

      const data = await response.json();
      setProducts(data.products || []);
      setSelectedProducts(new Set());
      setAiContent(null);
    } catch (error) {
      console.error('Search error:', error);
      alert('Failed to search products. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const toggleProductSelection = (index: number) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedProducts(newSelected);
  };

  const generateAIContent = async () => {
    if (selectedProducts.size === 0) {
      alert('Please select at least one product');
      return;
    }

    setGeneratingAI(true);
    try {
      const selectedProductData = Array.from(selectedProducts).map(index => ({
        name: products[index].title,
        description: products[index].description,
        link: `https://innerunionhome.com/products/${products[index].handle}`,
        imageUrl: products[index].images[0]?.url || ''
      }));

      const response = await fetch(`${API_BASE}/api/generate-product-scenes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          products: selectedProductData,
          searchQuery: searchQuery
        }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to generate AI content');

      const data = await response.json();
      setAiContent({
        carouselPrompts: data.scenePrompts || [],
        caption: data.caption,
        hashtags: data.hashtags,
        scenes: data.scenes || [],
        downloads: data.downloads || {},
        catalog: data.catalog || []
      });
    } catch (error) {
      console.error('AI generation error:', error);
      alert('Failed to generate AI content. Check console for details.');
    } finally {
      setGeneratingAI(false);
    }
  };

  const downloadAllImages = async () => {
    if (selectedProducts.size === 0) {
      alert('Please select at least one product');
      return;
    }

    setDownloadingImages(true);
    try {
      const selectedProductData = Array.from(selectedProducts).map(index => products[index]);

      // Download main image from each selected product
      for (let i = 0; i < selectedProductData.length; i++) {
        const product = selectedProductData[i];
        const mainImage = product.images[0];

        if (!mainImage) continue;

        try {
          const response = await fetch(mainImage.url);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;

          // Extract filename from URL or create one
          const urlParts = mainImage.url.split('/');
          const filename = urlParts[urlParts.length - 1].split('?')[0] || `product-${i + 1}.jpg`;
          a.download = `${product.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${filename}`;

          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);

          // Small delay between downloads
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (error) {
          console.error(`Failed to download image for ${product.title}:`, error);
        }
      }

      alert(`Successfully initiated download for ${selectedProductData.length} image(s)`);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download images. Check console for details.');
    } finally {
      setDownloadingImages(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="bg-card rounded-lg shadow-md p-6 border border-border">
        <h2 className="text-2xl font-bold text-foreground mb-4">
          🔍 Product Retrieval & Content Generator
        </h2>
        <p className="text-muted-foreground mb-6">
          Search for products from your Shopify store, view all images, and generate AI-powered carousel prompts and ad storylines.
        </p>

        {/* Search Bar */}
        <div className="flex gap-3 mb-6">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && searchProducts()}
            placeholder="Search products (e.g., 'handwoven rug', 'brass lamp', 'velvet sofa')..."
            className="flex-1 px-4 py-3 bg-background border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder:text-muted-foreground"
          />
          <button
            onClick={searchProducts}
            disabled={loading || !searchQuery.trim()}
            className="px-6 py-3 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-semibold rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>

        {/* Product Results */}
        {products.length > 0 && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                Found {products.length} product{products.length !== 1 ? 's' : ''}
                {selectedProducts.size > 0 && (
                  <span className="ml-2 text-primary">
                    ({selectedProducts.size} selected)
                  </span>
                )}
              </h3>
              {selectedProducts.size > 0 && (
                <button
                  onClick={() => setSelectedProducts(new Set())}
                  className="text-sm text-muted-foreground hover:text-foreground underline"
                >
                  Clear Selection
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map((product, index) => (
                <div
                  key={product.id}
                  onClick={() => toggleProductSelection(index)}
                  className={`cursor-pointer border-2 rounded-lg overflow-hidden transition-all transform hover:scale-105 ${selectedProducts.has(index)
                      ? 'border-primary bg-primary/10 shadow-lg'
                      : 'border-border bg-card hover:border-primary/50 hover:shadow-md'
                    }`}
                >
                  <div className="relative">
                    {product.images[0] && (
                      <img
                        src={product.images[0].url}
                        alt={product.images[0].alt}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    {selectedProducts.has(index) && (
                      <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center font-bold shadow-lg">
                        ✓
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-foreground mb-2 line-clamp-2">
                      {product.title}
                    </h4>
                    <p className="text-sm text-muted-foreground">{product.images.length} images</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {selectedProducts.size > 0 && (
          <div className="border-t border-border pt-6 space-y-4">
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={generateAIContent}
                disabled={generatingAI}
                className="flex-1 min-w-[200px] px-6 py-3 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-semibold rounded-lg hover:opacity-90 disabled:opacity-50 transition-all"
              >
                {generatingAI ? '✨ Generating...' : '✨ Generate Scenes, Caption & Hashtags'}
              </button>
              <button
                onClick={downloadAllImages}
                disabled={downloadingImages}
                className="px-6 py-3 bg-secondary text-secondary-foreground font-semibold rounded-lg hover:bg-secondary/80 disabled:opacity-50 transition-all"
              >
                {downloadingImages ? '⬇️ Downloading...' : '⬇️ Download All Images'}
              </button>
              <button
                onClick={() => setShowPromptPlanner((prev) => !prev)}
                className="px-6 py-3 bg-card text-foreground font-semibold rounded-lg border border-border hover:bg-muted transition-all"
              >
                {showPromptPlanner ? 'Hide Manual Prompt Planner' : '🧠 Manual Prompt Planner'}
              </button>
            </div>
            {showPromptPlanner && (
              <div className="mt-6">
                <VideoPromptGenerator embedded initialTitle={`Manual prompts for ${searchQuery}`} />
              </div>
            )}
          </div>
        )}

        {/* Generated Content Display */}
        {aiContent && (
          <div className="border-t border-border pt-6 space-y-6">
            <h3 className="text-xl font-bold text-foreground">Generated Content</h3>

            <div className="space-y-4">
              {/* Scene Plans */}
              {aiContent.scenes && aiContent.scenes.length > 0 && (
                <div className="bg-muted/30 rounded-lg p-6 space-y-4 border border-border">
                  <div className="flex flex-wrap gap-3 items-center">
                    <h4 className="text-xl font-bold text-foreground">🧾 Scene Plans & Product Placement</h4>
                    {aiContent.downloads?.bundle && (
                      <button
                        onClick={downloadAllBriefs}
                        className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90"
                      >
                        Download All Briefs
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Every scene spells out whether to feature specific products or lean on conceptual visuals. Use these briefs with your photographer or AI image tool.
                  </p>
                  <div className="space-y-4">
                    {aiContent.scenes.map((scene, idx) => (
                      <div key={idx} className="bg-card border border-border rounded-xl shadow-sm">
                        <div className="px-5 py-4 border-b border-border flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="text-xs uppercase tracking-wide text-primary font-semibold">Scene {idx + 1}</div>
                            <h5 className="text-lg font-bold text-foreground">{scene.sceneTitle}</h5>
                            <div className="text-xs font-medium text-muted-foreground">Visual type: {scene.visualType}</div>
                          </div>
                          <div className="flex gap-2">
                            {scene.download && (
                              <button
                                onClick={() => downloadTextFile(scene.download)}
                                className="px-3 py-2 bg-secondary text-secondary-foreground text-xs font-semibold rounded-lg hover:bg-secondary/80"
                              >
                                Download Brief
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="px-5 py-4 space-y-4">
                          <div>
                            <h6 className="text-sm font-semibold text-foreground mb-1">Image Prompt</h6>
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{scene.imagePrompt}</p>
                          </div>
                          {scene.nonProductVisual && (
                            <div className="bg-muted rounded-lg p-3 text-sm text-muted-foreground">
                              <span className="font-semibold text-foreground">Non-product Visual:</span> {scene.nonProductVisual}
                            </div>
                          )}
                          {scene.notes && (
                            <div className="bg-muted/50 border border-border rounded-lg p-3 text-sm text-muted-foreground">
                              <span className="font-semibold text-foreground">Notes:</span> {scene.notes}
                            </div>
                          )}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <h6 className="text-sm font-semibold text-foreground">Product Placement</h6>
                              <span className="text-xs text-muted-foreground">({scene.products.length} item{scene.products.length !== 1 ? 's' : ''})</span>
                            </div>
                            {scene.products.length === 0 ? (
                              <p className="text-sm text-muted-foreground">No specific products required—focus on concept or educational visuals.</p>
                            ) : (
                              <div className="space-y-3">
                                {scene.products.map((placement, placementIdx) => (
                                  <div
                                    key={placement.id || placementIdx}
                                    className="border border-border rounded-lg p-3 bg-muted/20"
                                  >
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                      <div className="text-sm font-semibold text-foreground">
                                        {placementIdx + 1}. {placement.name}
                                        <span className="ml-2 text-xs uppercase tracking-wide text-primary">{placement.role}</span>
                                        {placement.optional && <span className="ml-2 text-xs text-muted-foreground">(optional)</span>}
                                      </div>
                                      {placement.link && (
                                        <a
                                          href={placement.link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-primary hover:text-primary/80 font-semibold"
                                        >
                                          View Product ↗
                                        </a>
                                      )}
                                    </div>
                                    <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">{placement.fitExplanation}</p>
                                    {placement.placementLocation && (
                                      <p className="mt-1 text-xs text-muted-foreground">
                                        <span className="font-semibold text-foreground">Location:</span> {placement.placementLocation}
                                      </p>
                                    )}
                                    {placement.impactNote && (
                                      <p className="mt-1 text-xs text-muted-foreground">
                                        <span className="font-semibold text-foreground">Impact:</span> {placement.impactNote}
                                      </p>
                                    )}
                                    {placement.highlight && (
                                      <p className="mt-1 text-xs text-muted-foreground">{placement.highlight}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Carousel Prompts */}
              <div className="bg-muted/30 rounded-lg p-6 border border-border">
                <h4 className="font-bold text-foreground mb-3 flex items-center gap-2">
                  📸 Carousel Image Prompts
                  <button
                    onClick={() => copyToClipboard(aiContent.carouselPrompts.join('\n\n'))}
                    className="ml-auto px-3 py-1 bg-background border border-border text-foreground rounded-md text-sm hover:bg-muted"
                  >
                    Copy All
                  </button>
                </h4>
                <div className="space-y-3">
                  {aiContent.carouselPrompts.map((prompt, idx) => (
                    <div key={idx} className="bg-card rounded-lg p-4 shadow-sm border border-border">
                      <div className="flex justify-between items-start mb-2">
                        3              <span className="font-semibold text-primary">Slide {idx + 1}</span>
                        <button
                          onClick={() => copyToClipboard(prompt)}
                          className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded hover:bg-muted/80"
                        >
                          Copy
                        </button>
                      </div>
                      <p className="text-foreground">{prompt}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ad Storyline */}
              {aiContent.adStorylineShots && aiContent.adStorylineShots.length > 0 ? (
                <div className="bg-muted/30 rounded-lg p-6 border border-border">
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="font-bold text-foreground">🎬 Ad Storyline Shots</h4>
                    <button
                      onClick={() => copyToClipboard(JSON.stringify(aiContent.adStorylineShots, null, 2))}
                      className="ml-auto px-3 py-1 bg-background border border-border text-foreground rounded-md text-sm hover:bg-muted"
                    >
                      Copy JSON
                    </button>
                  </div>
                  <div className="space-y-4">
                    {aiContent.adStorylineShots.map((shot, idx) => (
                      <div key={idx} className="bg-card rounded-lg p-4 shadow-sm border border-border">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="text-xs uppercase tracking-wide text-primary font-semibold">Shot {idx + 1}</span>
                            <h5 className="text-lg font-bold text-foreground">{shot.shotTitle || `Shot ${idx + 1}`}</h5>
                          </div>
                          <button
                            onClick={() => copyToClipboard(`${shot.scenePrompt}\nVO: ${shot.voiceover}\nText: ${shot.onScreenText}`)}
                            className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded hover:bg-muted/80"
                          >
                            Copy Shot
                          </button>
                        </div>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <p><strong className="text-foreground">Scene Prompt:</strong> {shot.scenePrompt}</p>
                          <p><strong className="text-foreground">Voiceover:</strong> {shot.voiceover}</p>
                          <p><strong className="text-foreground">On-screen Text:</strong> {shot.onScreenText}</p>
                          <p><strong className="text-foreground">Purpose:</strong> {shot.purpose}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : aiContent.adStoryline ? (
                <div className="bg-muted/30 rounded-lg p-6 border border-border">
                  <h4 className="font-bold text-foreground mb-3 flex items-center gap-2">
                    🎬 Ad Storyline
                    <button
                      onClick={() => copyToClipboard(aiContent.adStoryline || '')}
                      className="ml-auto px-3 py-1 bg-background text-foreground rounded-md text-sm hover:bg-muted border border-border"
                    >
                      Copy
                    </button>
                  </h4>
                  <div className="bg-card rounded-lg p-4 shadow-sm border border-border">
                    <p className="text-foreground whitespace-pre-wrap">{aiContent.adStoryline}</p>
                  </div>
                </div>
              ) : null}

              {/* Caption */}
              {aiContent.caption && (
                <div className="bg-muted/30 rounded-lg p-6 border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-foreground">📝 Caption</h4>
                    <button
                      onClick={() => copyToClipboard(aiContent.caption || '')}
                      className="px-3 py-1 bg-background text-foreground rounded-md text-sm hover:bg-muted border border-border"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="bg-card rounded-lg p-4 shadow-sm border border-border">
                    <p className="text-foreground whitespace-pre-wrap">{aiContent.caption}</p>
                  </div>
                </div>
              )}

              {/* Hashtags */}
              {aiContent.hashtags && aiContent.hashtags.length > 0 && (
                <div className="bg-muted/30 rounded-lg p-6 border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-foreground">
                      #️⃣ Hashtags ({aiContent.hashtags.length})
                    </h4>
                    <button
                      onClick={() => copyToClipboard(aiContent.hashtags?.join(' ') || '')}
                      className="px-3 py-1 bg-background text-foreground rounded-md text-sm hover:bg-muted border border-border"
                    >
                      Copy All
                    </button>
                  </div>
                  <div className="bg-card rounded-lg p-4 shadow-sm border border-border">
                    <div className="flex flex-wrap gap-2">
                      {aiContent.hashtags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div >
  );
};

export default ProductRetrieval;
